import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import {
  reprocessTrack,
  bulkUpdateTracks,
  bulkDeleteTracks,
  type BulkUpdateTrackField,
  type BulkUpdateTracksBody,
} from '../../api/libraryApi';
import { ReprocessConfirmDialog } from './components/ReprocessConfirmDialog';
import { BulkEditTracksModal } from './components/BulkEditTracksModal';
import { ManagePlaylistsModal } from './components/ManagePlaylistsModal';
import { LibraryBrowserPanel } from '../../components/LibraryBrowserPanel';

export function LibraryListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [reprocessTrackId, setReprocessTrackId] = useState<string | null>(null);
  const [bulkEditIds, setBulkEditIds] = useState<string[] | null>(null);
  const [managePlaylistIds, setManagePlaylistIds] = useState<string[] | null>(null);
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);
  const deleteCancelRef = useRef<HTMLButtonElement>(null);

  const reprocessMutation = useMutation({
    mutationFn: (trackId: string) => reprocessTrack(trackId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-library'] });
      setReprocessTrackId(null);
    },
  });

  const bulkEditMutation = useMutation({
    mutationFn: ({
      trackIds,
      fields,
      values,
    }: {
      trackIds: string[];
      fields: BulkUpdateTrackField[];
      values: Omit<BulkUpdateTracksBody, 'trackIds' | 'fields'>;
    }) => bulkUpdateTracks(trackIds, fields, values),
    onMutate: ({ trackIds }) => {
      toast({
        title: t('admin.library.bulkEdit.updating', { count: trackIds.length }),
        status: 'loading',
        duration: null,
        id: 'bulk-edit-progress',
      });
    },
    onSuccess: (res) => {
      // Affected tracks live everywhere; refresh the library + filter vocab.
      queryClient.invalidateQueries({ queryKey: ['admin-library'] });
      queryClient.invalidateQueries({ queryKey: ['admin-library-infinite'] });
      queryClient.invalidateQueries({ queryKey: ['admin-library-filter-options'] });
      // Edited tracks may live in curated playlists — refresh those too (review P1).
      queryClient.invalidateQueries({ queryKey: ['admin-playlist'] });
      toast.close('bulk-edit-progress');
      toast({
        title: t('admin.library.bulkEdit.updated', { count: res.updatedCount }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setBulkEditIds(null);
    },
    onError: () => {
      toast.close('bulk-edit-progress');
      toast({
        title: t('admin.library.bulkEdit.updateFailed'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    },
  });

  const handleApplyBulkEdit = useCallback(
    (fields: BulkUpdateTrackField[], values: Omit<BulkUpdateTracksBody, 'trackIds' | 'fields'>) => {
      if (!bulkEditIds || bulkEditIds.length === 0) return;
      bulkEditMutation.mutate({ trackIds: bulkEditIds, fields, values });
    },
    [bulkEditIds, bulkEditMutation],
  );

  const bulkDeleteMutation = useMutation({
    mutationFn: (trackIds: string[]) => bulkDeleteTracks(trackIds),
    onMutate: (trackIds) => {
      toast({
        title: t('admin.library.bulkDelete.deleting', { count: trackIds.length }),
        status: 'loading',
        duration: null,
        id: 'bulk-delete-progress',
      });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-library'] });
      queryClient.invalidateQueries({ queryKey: ['admin-library-infinite'] });
      queryClient.invalidateQueries({ queryKey: ['admin-library-filter-options'] });
      // Deleted tracks are removed from any curated playlist they were in.
      queryClient.invalidateQueries({ queryKey: ['admin-playlist'] });
      toast.close('bulk-delete-progress');
      toast({
        title: t('admin.library.bulkDelete.deleted', { count: res.deletedCount }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setDeleteIds(null);
    },
    onError: () => {
      toast.close('bulk-delete-progress');
      toast({
        title: t('admin.library.bulkDelete.failed'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    },
  });

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="library-list-page">
      <LibraryBrowserPanel
        mode="standalone"
        onTrackClick={(trackId) => navigate(`/library/${trackId}`)}
        onReprocess={(trackId) => setReprocessTrackId(trackId)}
        onEditSelected={(trackIds) => setBulkEditIds(trackIds)}
        onManagePlaylists={(trackIds) => setManagePlaylistIds(trackIds)}
        onDeleteSelected={(trackIds) => setDeleteIds(trackIds)}
        actionButton={
          <Button
            variant="brand"
            onClick={() => navigate('/library/upload')}
            data-testid="btn-upload-tracks"
          >
            {t('admin.library.uploadTracks')}
          </Button>
        }
      />

      {reprocessTrackId && (
        <ReprocessConfirmDialog
          trackId={reprocessTrackId}
          open={Boolean(reprocessTrackId)}
          onClose={() => setReprocessTrackId(null)}
          onConfirm={() => reprocessMutation.mutate(reprocessTrackId)}
          isConfirming={reprocessMutation.isPending}
        />
      )}

      {bulkEditIds && (
        <BulkEditTracksModal
          open={Boolean(bulkEditIds)}
          trackIds={bulkEditIds}
          onClose={() => setBulkEditIds(null)}
          onApply={handleApplyBulkEdit}
          isApplying={bulkEditMutation.isPending}
        />
      )}

      {managePlaylistIds && (
        <ManagePlaylistsModal
          open={Boolean(managePlaylistIds)}
          trackIds={managePlaylistIds}
          onClose={() => setManagePlaylistIds(null)}
          onDone={() => {
            queryClient.invalidateQueries({ queryKey: ['admin-library'] });
            toast({ title: t('admin.playlists.manage.done'), status: 'success', duration: 3000, isClosable: true });
            setManagePlaylistIds(null);
          }}
        />
      )}

      <AlertDialog
        isOpen={Boolean(deleteIds)}
        leastDestructiveRef={deleteCancelRef}
        onClose={() => setDeleteIds(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent data-testid="library-bulk-delete-dialog">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('admin.library.bulkDelete.title', { count: deleteIds?.length ?? 0 })}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('admin.library.bulkDelete.confirm', { count: deleteIds?.length ?? 0 })}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={deleteCancelRef} onClick={() => setDeleteIds(null)} data-testid="library-bulk-delete-cancel">
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                isLoading={bulkDeleteMutation.isPending}
                onClick={() => deleteIds && bulkDeleteMutation.mutate(deleteIds)}
                data-testid="library-bulk-delete-confirm"
              >
                {t('admin.library.bulkDelete.action', 'Delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
